import React, { useEffect, useState } from 'react'
import { Collapse } from 'react-bootstrap'
import style from '../../../../Styles/index.module.scss'
import {
    DragDropContext,
    Draggable,
    DraggableProvided,
    DraggableStateSnapshot,
    Droppable,
    DroppableProvided,
} from '@hello-pangea/dnd'
import _cloneDeep from 'lodash/cloneDeep'
import { useAppDispatch } from '../../../../../../../hooks/useAppDispatch'
import { PATH_JOINER } from '../../../../../utils/getConfigBitPath'
import { useExpand } from '../../../../../../../hooks/useExpand'
import { hasDatePicker } from '../../../../utils/hasDatePicker'
import { generateElementID } from '../../../../../../../utils/generateElementID'
import {
    deleteRuleBlockArrayItem,
    modifyRuleCardConfig,
} from '../../../../../../../store/actions/ruleCardActions'
import { ruleCardActions } from '../../../../../../../store/slices/ruleCardSlice'
import { ViewOnlyWrapper } from '../../../../../../ViewOnly/ViewOnlyWrapper'
import { TableVirtuoso } from 'react-virtuoso'
import { NoData } from '../../../../../../NoData'
import { BlockArrayTableHeadControls } from '../../../BlockArrayTable/BlockArrayTableHeadControls'
import { BlockArrayTableHead } from '../../../BlockArrayTable/BlockArrayTableHead'
import { BlockArrayTableRow } from '../../../BlockArrayTable/BlockArrayTableRow'
import styles from '../../../BlockArrayTable/BlockArrayTable.module.scss'

interface Props {
    dataPath: string
    pathWithElementID: string | undefined
    config: any
    schema: any
    name: string
    className?: string
    levelDeep?: number
    isViewOnly?: boolean
    element_id?: string
    inCard?: boolean
    isRuleActive?: boolean
    isParentDisabled?: boolean
}

export const RuleBlockArrayTable = ({
    dataPath,
    pathWithElementID,
    config = [],
    schema = {},
    name,
    className,
    isViewOnly,
    element_id,
    inCard,
    isRuleActive,
    isParentDisabled,
}: Props) => {
    const {
        is_order_user_defined: isOrderUserDefined,
        is_required: isRequired,
    } = schema
    const dispatch = useAppDispatch()

    const [isAddingNewRule, setIsAddingNewRule] = useState(false)
    const [focusIndex, setFocusIndex] = useState<number>(-1)
    const path = dataPath.split(PATH_JOINER)

    const componentSchema = schema?.children?.children || schema?.children
    const { is_auxiliary: isCollapsed } = schema?.children || schema?.children?.children
    const localId = element_id ? `${element_id}-${name}` : `${path}-${name}`

    const { expandBtn, expanded, toggle } = useExpand({
        isExpanded: !isCollapsed,
        uniqueID: localId,
        ariaID: '',
        className: `${style.button} alwaysActive`,
    })

    const errorKey = pathWithElementID
        ? pathWithElementID
        : path.join(PATH_JOINER)

    const heightForDatePicker = hasDatePicker(schema)

    const tableVirtuosoHeight =
        config.length > 10
            ? `calc(2.25rem + ${10 * 54}px)`
            : `calc(2.25rem + ${config.length * 54}px)`

    const focusItem = (index: number) => setFocusIndex(index)
    const setIsAdding = (value: boolean) => setIsAddingNewRule(value)

    useEffect(() => {
        const noElemIdItemIndexes = config.reduce(
            (acc: any[], item: any, idx: number) => {
                if (item && !item.element_id) {
                    acc.push(idx)
                }
                return acc
            },
            []
        )

        if (noElemIdItemIndexes.length) {
            const configClone = _cloneDeep(config)
            noElemIdItemIndexes.reduce(
                (acc: any, noElemIdItemIndex: number) => {
                    acc[noElemIdItemIndex].element_id = generateElementID()
                    return acc
                },
                configClone
            )
            dispatch(modifyRuleCardConfig({ path, data: configClone }))
        }
    }, []) //eslint-disable-line

    useEffect(() => {
        if (isRequired && !config?.length) {
            dispatch(
                ruleCardActions.setRuleConfigInvalid({ errorKeyName: errorKey })
            )
        }
        if (isRequired && config?.length) {
            dispatch(
                ruleCardActions.setRuleConfigValid({ errorKeyName: errorKey })
            )
        }
    }, [config]) // eslint-disable-line

    const handleOnDragEnd = (result: any): void => {
        if (!result.destination) return

        const items = [...config]
        const [reorderedItem] = items.splice(result.source.index, 1)
        items.splice(result.destination.index, 0, reorderedItem)
        dispatch(modifyRuleCardConfig({ path, data: items }))
    }

    const handleDeleteAll = () => {
        dispatch(modifyRuleCardConfig({ path, data: [] }))
    }

    const handleDeleteOneItem = (index: number) => {
        dispatch(deleteRuleBlockArrayItem({ path, index }))
    }

    const handlePositionChange = (idx: number) => (toPosition: number) => {
        const dataItems = [...config]
        const [reorderedItem] = dataItems.splice(Number(idx), 1)
        const moveTo = Number(toPosition) - 1
        dataItems.splice(moveTo, 0, reorderedItem)
        dispatch(modifyRuleCardConfig({ path, data: dataItems }))
    }

    return (
        <ViewOnlyWrapper
            disableEvents={!!isViewOnly}
            className={`${className} rounded-bottom mt-2 mb-2 position-relative w-100`}
        >
            <BlockArrayTableHeadControls
                schema={schema}
                config={config}
                path={path}
                name={name}
                isParentDisabled={isParentDisabled}
                inCard={inCard}
                isRuleActive={isRuleActive}
                componentSchema={componentSchema}
                description={schema?.description}
                toggle={toggle}
                onDeleteAll={handleDeleteAll}
                isRuleCard
            >
                {expandBtn}
            </BlockArrayTableHeadControls>

            <Collapse in={expanded}>
                <div className="w-100">
                    <DragDropContext onDragEnd={handleOnDragEnd}>
                        <Droppable
                            droppableId="app-table"
                            direction="vertical"
                        >
                            {(provided: DroppableProvided) => (
                                <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                >
                                    <TableVirtuoso
                                        style={{
                                            minHeight: heightForDatePicker
                                                ? '50vh'
                                                : '112px',
                                            height: tableVirtuosoHeight,
                                        }}
                                        overscan={{ main: 10, reverse: 10 }}
                                        context={{ data: config }}
                                        components={{
                                            Table: (props) => (
                                                <table
                                                    className="m-0 table app-table table-hover p-relative w-100"
                                                    {...props}
                                                />
                                            ),
                                            TableRow: (props) => {
                                                const {
                                                    context,
                                                    ...otherProps
                                                } = props
                                                const data = context?.data
                                                const idx = props['data-index']
                                                const item = data?.[idx]
                                                const elementId =
                                                    item?.element_id
                                                return (
                                                    <Draggable
                                                        isDragDisabled={
                                                            !isOrderUserDefined
                                                        }
                                                        key={String(
                                                            elementId
                                                                ? elementId
                                                                : idx
                                                        )}
                                                        draggableId={String(
                                                            elementId
                                                                ? elementId
                                                                : idx
                                                        )}
                                                        index={idx}
                                                    >
                                                        {(
                                                            provided: DraggableProvided,
                                                            snapshot: DraggableStateSnapshot
                                                        ) => (
                                                            <tr
                                                                className={
                                                                    styles.tRow
                                                                }
                                                                {...otherProps}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}
                                                                ref={
                                                                    provided.innerRef
                                                                }
                                                                style={
                                                                    snapshot.isDragging
                                                                        ? {
                                                                              display:
                                                                                  'table',
                                                                              background:
                                                                                  '#ececec',
                                                                              tableLayout:
                                                                                  'fixed',
                                                                              ...provided
                                                                                  .draggableProps
                                                                                  .style,
                                                                          }
                                                                        : {
                                                                              display:
                                                                                  'table-row',
                                                                              cursor: 'pointer',
                                                                              ...provided
                                                                                  .draggableProps
                                                                                  .style,
                                                                          }
                                                                }
                                                            >
                                                                <BlockArrayTableRow
                                                                    data={item}
                                                                    items={
                                                                        config
                                                                    }
                                                                    name={name}
                                                                    index={idx}
                                                                    focusIndex={
                                                                        focusIndex
                                                                    }
                                                                    onItemFocus={
                                                                        focusItem
                                                                    }
                                                                    onDeleteItem={
                                                                        handleDeleteOneItem
                                                                    }
                                                                    onPositionChange={
                                                                        handlePositionChange
                                                                    }
                                                                    schema={
                                                                        componentSchema
                                                                    }
                                                                    parentSchema={
                                                                        schema
                                                                    }
                                                                    dataPath={
                                                                        path
                                                                    }
                                                                    isOrderUserDefined={
                                                                        isOrderUserDefined
                                                                    }
                                                                    isRuleCard
                                                                    {...provided}
                                                                />
                                                            </tr>
                                                        )}
                                                    </Draggable>
                                                )
                                            },
                                        }}
                                        data={config}
                                        fixedHeaderContent={() => (
                                            <BlockArrayTableHead
                                                dataPath={dataPath}
                                                componentSchema={
                                                    componentSchema
                                                }
                                                isAddingNewRule={
                                                    isAddingNewRule
                                                }
                                                setIsAdding={setIsAdding}
                                            />
                                        )}
                                    />
                                    {config.length === 0 ? (
                                        <NoData
                                            style={{
                                                marginTop: '4px',
                                                marginBottom: '4px',
                                                height: '2rem',
                                            }}
                                        />
                                    ) : null}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>
            </Collapse>
        </ViewOnlyWrapper>
    )
}
